import { useState } from "react";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";
import { SavedCompany } from "@/types/user";
import { Company } from "@/types/company";

export interface UpdateSavedCompanyData {
  notes?: SavedCompany["notes"];
  has_applied?: SavedCompany["has_applied"];
  saved_reason?: SavedCompany["saved_reason"];
}

export interface UpdateSavedCompanyResponse {
  success: boolean;
  message: string;
  saved_company: {
    id: SavedCompany["id"];
    company_name: Company["company_name"];
    notes: SavedCompany["notes"];
    has_applied: SavedCompany["has_applied"];
    saved_reason: SavedCompany["saved_reason"];
    interaction_count: SavedCompany["interaction_count"];
    last_interaction_at: SavedCompany["last_interaction_at"];
  };
}

export const useUpdateSavedCompany = () => {
  const [data, setData] = useState<UpdateSavedCompanyResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const updateSavedCompany = async (
    savedCompanyId: SavedCompany["id"],
    updateData: UpdateSavedCompanyData,
  ) => {
    setLoading(true);
    setError(null);

    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("Not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.put<UpdateSavedCompanyResponse>(
        `users/users/update_saved_company/?saved_company_id=${savedCompanyId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setData(response.data);
      return response.data;
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, updateSavedCompany };
};
